import { useEffect, useState } from 'react';

import { has } from 'lodash';

import { setFormError } from 'utils/form';
import { unMaskFormFields } from 'utils/unmask';

import { useHistory } from 'react-router';

import { useTranslation } from 'react-i18next';

import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';

import { useForm, SubmitHandler, ErrorOption } from 'react-hook-form';

import { Button } from 'ui';
import useToastContext from 'ui/hooks/useToast';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useLoading } from 'ui/contexts/overlay/Loading';

import { Page } from 'dashboard/components/dashboard/Breadcrumbs';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';

import {
  Contacts,
  Entities,
  ContactTypes,
} from 'contactGroups/models/contactGroups';

import ContactGroupsFormFieldsComponent, {
  ContactGroupsFormErrors,
  ContactGroupsFormFields,
  ContactGroupsFormInputs,
} from 'contactGroups/components/form/ContactGroupsFormFields';

import {
  ContactGroupCreated,
  ContactGroupMutationVariables,
  CONTACT_GROUP_CREATE_MUTATION,
} from 'contactGroups/graphql/contactGroupsCreateMutation';

import {
  ContactGroupList,
  CONTACT_GROUPS_QUERY,
  contactGroupsTypename,
} from 'contactGroups/graphql/contactGroupsQuery';

import {
  ConsumerUnitsSelectList,
  CONSUMER_UNITS_SELECT_QUERY,
  CONSUMER_UNITS_SELECT_QUERY_VARIABLES,
} from 'consumerUnits/graphql/consumerUnitsSelectQuery';

import {
  GenerationUnitsSelectList,
  GENERATION_UNITS_SELECT_QUERY,
  GENERATION_UNITS_SELECT_QUERY_VARIABLES,
} from 'generationUnits/graphql/generationUnitsSelectQuery';

import {
  CooperativesSelectList,
  COOPERATIVES_SELECT_QUERY,
  COOPERATIVES_SELECT_QUERY_VARIABLES,
} from 'cooperatives/graphql/cooperativesSelectQuery';

const DASHBOARD_CONTACT_GROUPS_ROUTE = '/dashboard/contact-groups';

const BREADCRUMB_PAGES: Page[] = [
  {
    current: false,
    name: 'Grupos & Contatos',
    route: DASHBOARD_CONTACT_GROUPS_ROUTE,
  },
  {
    route: null,
    current: true,
    name: 'Novo Grupos & Contatos',
  },
];
const TITLE = 'Novo Grupos & Contatos';

const CREATE_ERROR_TOAST: ToastProps = {
  variant: 'danger',
  title: 'Algo deu errado!',
  text: 'Houve um erro ao tentar criar o cadastro de Grupos & Contatos',
};

const CREATE_SUCCESS_TOAST: ToastProps = {
  title: 'Sucesso!',
  variant: 'primary',
  text: 'Sucesso ao cadastrar Grupos & Contatos',
};

export default function CreateContactGroupsPage() {
  const { push } = useHistory();
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const {
    control,
    register,
    setError,
    setValue,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactGroupsFormInputs>();

  const [contactList, setContactList] = useState<Contacts[]>([]);

  const [entitiesList, setEntitiesList] = useState<Entities>({
    cooperative: [],
    consumerUnit: [],
    generationUnit: [],
  });

  const [
    consumerUnitsSelect,
    { data: consumerUnitsSelectData, loading: consumerUnitsSelectLoading },
  ] = useLazyQuery<ConsumerUnitsSelectList>(
    CONSUMER_UNITS_SELECT_QUERY,
    CONSUMER_UNITS_SELECT_QUERY_VARIABLES
  );

  const [
    cooperativesSelect,
    { data: cooperativesSelectData, loading: cooperativesSelectLoading },
  ] = useLazyQuery<CooperativesSelectList>(
    COOPERATIVES_SELECT_QUERY,
    COOPERATIVES_SELECT_QUERY_VARIABLES
  );

  const [
    generationUnitsSelect,
    { data: generationUnitsSelectData, loading: generationUnitsSelectLoading },
  ] = useLazyQuery<GenerationUnitsSelectList>(
    GENERATION_UNITS_SELECT_QUERY,
    GENERATION_UNITS_SELECT_QUERY_VARIABLES
  );

  useEffect(() => {
    consumerUnitsSelect();
  }, [consumerUnitsSelect]);

  useEffect(() => {
    cooperativesSelect();
  }, [cooperativesSelect]);

  useEffect(() => {
    generationUnitsSelect();
  }, [generationUnitsSelect]);

  const [contactGroupCreateMutation, { loading: loadingCreate }] = useMutation<
    ContactGroupCreated,
    ContactGroupMutationVariables
  >(CONTACT_GROUP_CREATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'message')) {
        addToast(CREATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof ContactGroupsFormFields, error);

          setTimeout(() => clearErrors(), 2500);
        },
        t
      );
    },
    onCompleted() {
      addToast(CREATE_SUCCESS_TOAST);
      push(DASHBOARD_CONTACT_GROUPS_ROUTE);
    },
  });

  const onSubmit: SubmitHandler<ContactGroupsFormFields> = (
    contactGroupCreateInput,
    event
  ) => {
    event?.preventDefault();

    contactGroupCreateInput = Object.assign(contactGroupCreateInput, {
      contacts: contactList,
      entities: entitiesList,
    });

    Object.keys(contactGroupCreateInput).forEach((key) => {
      !!!contactGroupCreateInput[key as keyof ContactGroupsFormFields] &&
        delete contactGroupCreateInput[key as keyof ContactGroupsFormFields];
    });

    contactGroupCreateInput = unMaskFormFields(contactGroupCreateInput);

    const hasLeastOneEntity =
      entitiesList.cooperative.length ||
      entitiesList.consumerUnit.length ||
      entitiesList.generationUnit.length;

    const hasLeastOneDomainContact = contactList.filter(
      (contact) => contact.contactType === ContactTypes.DOMAIN
    ).length;

    if (!hasLeastOneDomainContact) {
      setError('contacts', {
        type: 'manual',
        message: 'Este campo precisa ser preenchido',
      });
    }

    if (!hasLeastOneEntity) {
      setError('entities', {
        type: 'manual',
        message: 'Você precisa vincular pelo menos uma entidade ao grupo',
      });
    }

    setTimeout(() => clearErrors(), 3000);

    if (hasLeastOneEntity && hasLeastOneDomainContact) {
      contactGroupCreateMutation({
        variables: {
          contactGroupCreateInput,
        },
        update(cache, { data }) {
          const existingData = cache.readQuery<ContactGroupList>({
            query: CONTACT_GROUPS_QUERY,
          });
          cache.writeQuery({
            query: CONTACT_GROUPS_QUERY,
            data: {
              contactGroups: {
                __typename: contactGroupsTypename,
                afterCursor: existingData?.contactGroups.afterCursor || null,
                beforeCursor: existingData?.contactGroups.beforeCursor || null,
                entries: [
                  data?.contactGroupCreate,
                  ...(existingData?.contactGroups.entries || []),
                ],
              },
            },
          });
        },
      });
    }
  };

  const isLoading =
    loadingCreate ||
    cooperativesSelectLoading ||
    consumerUnitsSelectLoading ||
    generationUnitsSelectLoading;

  useEffect(() => {
    if (isLoading) {
      return showLoading();
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  const handleGetContactList = (contacts: Contacts[]) => {
    setContactList(contacts);
  };

  const handleGetEntityList = (entities: Entities) => {
    setEntitiesList(entities);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm breadcrumbPages={BREADCRUMB_PAGES} title={TITLE}>
            <Button type="submit" size="sm" disabled={isLoading}>
              Salvar
            </Button>
          </DashboardMainHeaderForm>
        }
      >
        <ContactGroupsFormFieldsComponent
          control={control}
          setValue={setValue}
          register={register}
          handleGetEntityList={handleGetEntityList}
          errors={errors as ContactGroupsFormErrors}
          handleGetContactList={handleGetContactList}
          cooperatives={cooperativesSelectData?.cooperatives?.entries || []}
          consumerUnits={consumerUnitsSelectData?.consumerUnits?.entries || []}
          generationUnits={generationUnitsSelectData?.generationUnits?.entries || []}
        />
      </Dashboard>
    </form>
  );
}
